export type ProductAnalyticsSurface = "web" | "native" | "backend";

export type ProductAnalyticsTopItem = {
  label: string;
  count: number;
};

export type AdminProductAnalyticsOverview = {
  activeMembers7d: number;
  activeMembers30d: number;
  screenViews7d: number;
  screenViews30d: number;
  signIns7d: number;
  onboardingSteps7d: number;
  onboardingCompleted7d: number;
  supportCardOpens7d: number;
  chatRoomOpens7d: number;
  chatRoomMessages7d: number;
  directMessages7d: number;
  adventureJoins7d: number;
  eventRsvps7d: number;
  memberEventDrafts7d: number;
  channelJoins7d: number;
  socialLinkClicks7d: number;
  onboardingCompletionRate7d: number;
  topScreens7d: ProductAnalyticsTopItem[];
  topActions7d: ProductAnalyticsTopItem[];
};

export const emptyAdminProductAnalyticsOverview = (): AdminProductAnalyticsOverview => ({
  activeMembers7d: 0,
  activeMembers30d: 0,
  screenViews7d: 0,
  screenViews30d: 0,
  signIns7d: 0,
  onboardingSteps7d: 0,
  onboardingCompleted7d: 0,
  supportCardOpens7d: 0,
  chatRoomOpens7d: 0,
  chatRoomMessages7d: 0,
  directMessages7d: 0,
  adventureJoins7d: 0,
  eventRsvps7d: 0,
  memberEventDrafts7d: 0,
  channelJoins7d: 0,
  socialLinkClicks7d: 0,
  onboardingCompletionRate7d: 0,
  topScreens7d: [],
  topActions7d: [],
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getAnalyticsRecord = (value: unknown) => {
  if (isRecord(value)) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.find(isRecord) ?? null;
  }

  return null;
};

const toFiniteNumber = (value: unknown) => {
  const numericValue =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim().length > 0
        ? Number(value)
        : 0;

  return Number.isFinite(numericValue) ? numericValue : 0;
};

const readNumber = (source: Record<string, unknown>, ...keys: string[]) => {
  for (const key of keys) {
    if (key in source) {
      return toFiniteNumber(source[key]);
    }
  }

  return 0;
};

const readString = (source: Record<string, unknown>, ...keys: string[]) => {
  for (const key of keys) {
    const value = source[key];

    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }

  return null;
};

const normalizeTopItems = (value: unknown): ProductAnalyticsTopItem[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.reduce<ProductAnalyticsTopItem[]>((items, item) => {
    if (Array.isArray(item)) {
      const label = typeof item[0] === "string" && item[0].trim().length > 0 ? item[0].trim() : null;

      if (label) {
        items.push({ label, count: toFiniteNumber(item[1]) });
      }

      return items;
    }

    if (!isRecord(item)) {
      return items;
    }

    const label = readString(item, "label", "name", "screen", "action");

    if (label) {
      items.push({
        label,
        count: readNumber(item, "count", "views", "event_count", "total"),
      });
    }

    return items;
  }, []);
};

const readTopItems = (source: Record<string, unknown>, ...keys: string[]) => {
  for (const key of keys) {
    if (key in source) {
      return normalizeTopItems(source[key]);
    }
  }

  return [];
};

export const normalizeAdminProductAnalyticsOverview = (value: unknown): AdminProductAnalyticsOverview => {
  const source = getAnalyticsRecord(value);

  if (!source) {
    return emptyAdminProductAnalyticsOverview();
  }

  return {
    activeMembers7d: readNumber(source, "activeMembers7d", "active_members_7d"),
    activeMembers30d: readNumber(source, "activeMembers30d", "active_members_30d"),
    screenViews7d: readNumber(source, "screenViews7d", "screen_views_7d"),
    screenViews30d: readNumber(source, "screenViews30d", "screen_views_30d"),
    signIns7d: readNumber(source, "signIns7d", "sign_ins_7d"),
    onboardingSteps7d: readNumber(source, "onboardingSteps7d", "onboarding_steps_7d"),
    onboardingCompleted7d: readNumber(source, "onboardingCompleted7d", "onboarding_completed_7d"),
    supportCardOpens7d: readNumber(source, "supportCardOpens7d", "support_card_opens_7d"),
    chatRoomOpens7d: readNumber(source, "chatRoomOpens7d", "chat_room_opens_7d"),
    chatRoomMessages7d: readNumber(source, "chatRoomMessages7d", "chat_room_messages_7d"),
    directMessages7d: readNumber(source, "directMessages7d", "direct_messages_7d"),
    adventureJoins7d: readNumber(source, "adventureJoins7d", "adventure_joins_7d"),
    eventRsvps7d: readNumber(source, "eventRsvps7d", "event_rsvps_7d"),
    memberEventDrafts7d: readNumber(source, "memberEventDrafts7d", "member_event_drafts_7d"),
    channelJoins7d: readNumber(source, "channelJoins7d", "channel_joins_7d"),
    socialLinkClicks7d: readNumber(source, "socialLinkClicks7d", "social_link_clicks_7d"),
    onboardingCompletionRate7d: readNumber(source, "onboardingCompletionRate7d", "onboarding_completion_rate_7d"),
    topScreens7d: readTopItems(source, "topScreens7d", "top_screens_7d"),
    topActions7d: readTopItems(source, "topActions7d", "top_actions_7d"),
  };
};

const SCREEN_PATTERNS: Array<{ pattern: RegExp; name: string }> = [
  { pattern: /^\/$/, name: "landing" },
  { pattern: /^\/landing$/, name: "landing_alt" },
  { pattern: /^\/signin$/, name: "sign_in" },
  { pattern: /^\/sign-in$/, name: "sign_in" },
  { pattern: /^\/signup$/, name: "sign_up" },
  { pattern: /^\/sign-up$/, name: "sign_up" },
  { pattern: /^\/verify-2fa$/, name: "verify_2fa" },
  { pattern: /^\/secure-account$/, name: "secure_account" },
  { pattern: /^\/onboarding$/, name: "onboarding" },
  { pattern: /^\/kinfeed$/, name: "kinfeed" },
  { pattern: /^\/community\/feed\/new$/, name: "kinfeed_compose" },
  { pattern: /^\/compose-post$/, name: "kinfeed_compose" },
  { pattern: /^\/community\/feed\/[^/]+$/, name: "kinfeed_post_detail" },
  { pattern: /^\/community\/channel\/[^/]+\/post\/[^/]+$/, name: "channel_reply_thread" },
  { pattern: /^\/communities$/, name: "community" },
  { pattern: /^\/community\/members$/, name: "community_members" },
  { pattern: /^\/community\/member\/[^/]+$/, name: "community_member_detail" },
  { pattern: /^\/community\/channel\/[^/]+$/, name: "channel_detail" },
  { pattern: /^\/community\/room\/new$/, name: "chat_room_create" },
  { pattern: /^\/community\/room\/edit\/[^/]+$/, name: "chat_room_edit" },
  { pattern: /^\/community\/room\/[^/]+\/edit$/, name: "chat_room_edit" },
  { pattern: /^\/community\/room\/[^/]+$/, name: "chat_room_detail" },
  { pattern: /^\/messages$/, name: "messages" },
  { pattern: /^\/messages\/[^/]+$/, name: "direct_message_thread" },
  { pattern: /^\/activities$/, name: "activities" },
  { pattern: /^\/adventures\/new$/, name: "activity_create" },
  { pattern: /^\/adventures\/edit\/[^/]+$/, name: "activity_edit" },
  { pattern: /^\/adventures\/[^/]+$/, name: "activity_detail" },
  { pattern: /^\/whats-on\/new$/, name: "member_event_create" },
  { pattern: /^\/whats-on\/edit\/[^/]+$/, name: "member_event_edit" },
  { pattern: /^\/whats-on\/[^/]+$/, name: "event_detail" },
  { pattern: /^\/support$/, name: "support" },
  { pattern: /^\/support\/kindred$/, name: "support_kindred" },
  { pattern: /^\/support\/category\/[^/]+$/, name: "support_category" },
  { pattern: /^\/support\/helper-pack\/[^/]+$/, name: "helper_pack" },
  { pattern: /^\/my-kindred$/, name: "menu" },
  { pattern: /^\/my-adventures$/, name: "my_adventures" },
  { pattern: /^\/settings$/, name: "settings" },
  { pattern: /^\/delete-account$/, name: "delete_account" },
  { pattern: /^\/notifications$/, name: "notifications" },
  { pattern: /^\/admin$/, name: "admin_dashboard" },
  { pattern: /^\/admin\/members$/, name: "admin_members" },
  { pattern: /^\/admin\/members\/[^/]+$/, name: "admin_member_detail" },
  { pattern: /^\/admin\/content$/, name: "admin_content" },
  { pattern: /^\/admin\/events$/, name: "admin_events" },
  { pattern: /^\/admin\/moderation$/, name: "admin_moderation" },
  { pattern: /^\/admin\/account-deletion$/, name: "admin_account_deletion" },
  { pattern: /^\/admin\/guidance$/, name: "admin_guidance" },
  { pattern: /^\/\(tabs\)$/, name: "kinfeed" },
  { pattern: /^\/\(tabs\)\/index$/, name: "kinfeed" },
  { pattern: /^\/\(tabs\)\/communities$/, name: "community" },
  { pattern: /^\/\(tabs\)\/activities$/, name: "activities" },
  { pattern: /^\/\(tabs\)\/support$/, name: "support" },
  { pattern: /^\/\(tabs\)\/my-kindred$/, name: "menu" },
  { pattern: /^\/community\/feed\/\[postId\]$/, name: "kinfeed_post_detail" },
  { pattern: /^\/community\/channel\/\[id\]$/, name: "channel_detail" },
  { pattern: /^\/community\/member\/\[id\]$/, name: "community_member_detail" },
  { pattern: /^\/community\/room\/\[roomId\]$/, name: "chat_room_detail" },
  { pattern: /^\/community\/room\/edit\/\[roomId\]$/, name: "chat_room_edit" },
  { pattern: /^\/adventures\/\[id\]$/, name: "activity_detail" },
  { pattern: /^\/whats-on\/\[id\]$/, name: "event_detail" },
  { pattern: /^\/admin\/members\/\[id\]$/, name: "admin_member_detail" },
];

export const normalizeKindredScreenName = (pathname: string) => {
  const normalizedPath = pathname.trim() || "/";

  for (const entry of SCREEN_PATTERNS) {
    if (entry.pattern.test(normalizedPath)) {
      return entry.name;
    }
  }

  return normalizedPath.replace(/^\//, "").replace(/\//g, "_") || "unknown";
};

export const formatKindredAnalyticsLabel = (value: string) =>
  value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
